import env, { setEnv } from "../utils/env";
import { getFlow } from "../api/flow";
import { devWarn } from "../utils/dev-logger";
import { tdLog } from "../utils/td-logger";
import {
  fetchFlowFailed,
  setGlobalDialog,
  fieldChanged,
  pharmaciesFound,
  capsulePharmacyDelivery,
  prevScreenFailed,
  prevScreenSuccess,
  addToast,
  resetFieldValidation,
  resetReduxStore,
  setSettings,
  addCallToActionButtonIsLoading,
  removeCallToActionButtonIsLoading,
  setFlowStatusLoading,
  removeToast,
  resetFormState,
  setFlowStatusLoaded
} from "./actions";
import getPharmacies, { capsuleDeliverabilityCheck } from "../api/pharmacies";

import getConsultState from "../api/consult-status";
import getSettings from "../api/settings";
import { pharmacyDataSelector, screensSelector } from "./selectors";
import Actions from "./action-types";
import {
  uploadPhotoRequest,
  uploadPhotoGenericRequest
} from "../api/photo-upload";
import mapData from "../utils/data-mapper";
import { enhanceDialogActionsWith } from "../context/messaging-layer";
import { constants } from "../resource/constants";
import { buildImageList } from "../utils/image-list-builder";
import { currentLocation, extractLocationName } from "../utils/location";
import { getLocationObjForCoordinates } from "../api/location";
import logTrackingEvents from "../api/tracker";
import { clearPersistedStoreInLocalStorage } from "../utils/local-storage-helper";
import {
  getRequestFormState,
  getRequestFormStateByUrl
} from "../utils/history-helper";
import {
  clearIndexedDB,
  clearIndexedDBIfNeeded,
  getAllFilesFromIndexedDB,
  setItemToIndexedDB
} from "../utils/localforage-utils";
import { convertToUtc, getLocalTimeZone } from "../utils/date-time-helper";
import { getAdjustedToCignaErrorText } from "../utils/cigna-date-time-picker-helper";
import { VIDEO_ROOM_PHOTO_MODAL_FIELD_IDENTIFIER } from "../utils/video-room-helper";
import {
  registrationService,
  responseAuthData
} from "../utils/registration-service";
import { loginToDashboard } from "../api/login";
import { prepareHttpHeaders } from "../utils/http";
import { downloadFile } from "../utils/download-file";
import { getFilteredFormState } from "../utils/form-state-filter";
import { getRequestDataForNotStoredPrevScreen } from "../utils/back-button-helper";

const FETCH_FLOW_ERROR_TOAST_ID = "__fetchFlow_";
const DOWNLOADING_FILE_ERROR_TOAST_ID = "__downloadFile__";
const SEARCH_PHARMACY_ERROR_TOAST_ID = "__searchPharmacy__";
const UPLOAD_RAV_PHOTO_ERROR_TOAST_ID = "__uploadRavPhoto__";
const FETCH_AVAILABLE_TIMES_ERROR_TOAST_ID = "__fetchAvailableTimes_";

const REDIRECT_TO_DASHBOARD = "redirect_to_dashboard";
const REDIRECT_TO_URL = "redirect_to_url";

const {
  FETCH_FLOW,
  INITIALIZE_FLOW,
  PREV_SCREEN,
  SET_STATE,
  NEXT_SCREEN,
  RESET_STATE,
  SET_CONSULT_STATE,
  UPDATE_HISTORY,
  UPDATE_HISTORY_ACTION,
  RESET_TRACKING_EVENTS,
  FETCH_AVAILABLE_TIMES_PENDING,
  FETCH_AVAILABLE_TIMES_SUCCESS,
  FETCH_AVAILABLE_TIMES_FAILURE,
  SET_FORM_ERRORS_FROM_BACK
} = Actions;

export function fetchFlow({
  url,
  formState,
  replaceHistory = 0,
  initialScreen,
  screenToSave,
  screenParams = {},
  isPageReloaded = false,
  isUnifiedRegistration = false
}) {
  tdLog("fetchFlow", url);
  return function (dispatch, getState) {
    const abortController = new AbortController();
    const { tdFlow } = getState();
    window[constants.abortControllerKey] = abortController;
    dispatch({ type: FETCH_FLOW });
    const newScreenParams = { ...screenParams };
    return getFlow({
      url,
      formState,
      translations: isUnifiedRegistration ? tdFlow?.settings?.translations : {},
      abortController
    })
      .then(res => {
        if (res[REDIRECT_TO_DASHBOARD]) {
          const { password, username } = res;

          loginToDashboard({ password, username });

          throw REDIRECT_TO_DASHBOARD;
        }

        if (res[REDIRECT_TO_URL]) {
          window.location.href = res[REDIRECT_TO_URL];

          throw REDIRECT_TO_URL;
        }

        if (res.errors && res.errors.length) {
          dispatch({
            type: SET_FORM_ERRORS_FROM_BACK,
            payload: { errors: res.errors }
          });
          throw new Error(res.error || "");
        }

        return mapData(res, tdFlow.settings);
      })
      .then(data => {
        const sharedScreens = tdFlow.screenParams?.sharedScreens ?? [];
        const internalSdkData = internalSdkAuth(data);
        const authData = responseAuthData(data);

        // This enables the am_internal_sdk flow in MAPI
        // by capturing the response_data if the AM sdk partner has permission
        // to access the RAV flow for US based consults via TAS.
        // These headers are set in http.js
        if (internalSdkData) {
          setEnv({
            AUTHENTICATION_DATA: JSON.stringify({
              internal_sdk: internalSdkData
            })
          });
        }

        // Set auth headers to requests
        if (authData) {
          registrationService(authData);
        }

        // return to previous screen(medical info) in case we successfully update medical history
        if (!data.show_screens) {
          if (url.includes("ehr/update")) {
            const previousScreenExists = tdFlow.history.length > 1;

            if (previousScreenExists) {
              dispatch(prevScreen());
            } else {
              dispatch(setFlowStatusLoaded());
            }

            return;
          }
          if (data.hasOwnProperty("from_message_room")) {
            dispatch(returnToInbox());
            return;
          }
        }

        if (isUnifiedRegistration) {
          formState = getFilteredFormState({
            fields: tdFlow.data?.screens?.[0]?.fields,
            formState
          });
        }

        const newSharedScreens = newScreenParams?.sharedScreens ?? [];

        // inject shared screens into the response
        data.screens = [...data.screens, ...sharedScreens, ...newSharedScreens];
        if (
          screenToSave &&
          !data.screens.find(screen => screen.name === screenToSave.name)
        ) {
          data.screens = [...data.screens, screenToSave];
        }

        dispatch(
          fetchFlowSuccess({
            url,
            data,
            formState,
            replaceHistory,
            initialScreen,
            screenParams: newScreenParams,
            isPageReloaded
          })
        );
      })
      .catch(err => {
        if ([REDIRECT_TO_DASHBOARD, REDIRECT_TO_URL].includes(err)) return;

        devWarn("Error Fetching Flow:", err);
        dispatch(fetchFlowFailed(err));
        if (!isUnifiedRegistration) {
          dispatch(
            addToast({
              id: FETCH_FLOW_ERROR_TOAST_ID,
              message: err.message
            })
          );
        } else if (err) {
          dispatch({
            type: SET_FORM_ERRORS_FROM_BACK,
            payload: { error: err.message }
          });
        }
      });
  };
}

export function returnToInbox() {
  return function (dispatch, getState) {
    const {
      tdFlow: { history }
    } = getState();
    const steps = history.length - 1;

    dispatch(prevScreen(steps, true));
  };
}

// Returns the deeply nested internal_sdk value if it exists
export function internalSdkAuth(data) {
  return data?.response_data?.authentication?.data?.internal_sdk;
}

export function fetchFlowSuccess(response) {
  tdLog("fetchFlowSuccess", response.url);
  return function (dispatch, getState) {
    const { tdFlow } = getState();
    // If the screen object has a suicide alert action on it, show the corresponding dialog first.
    if (
      response.data?.actions?.[0] &&
      response.data?.actions?.[0].type !== "trackEvent" &&
      response.data?.actions?.[0].type !== "pop_flow" &&
      response.data?.actions?.[0].type !== "pop_screen" &&
      response.data?.actions?.[0].value !==
        "/screens/request_visit/member_attestation"
    ) {
      const action = response.data.actions[0];
      const { type, value } = action;
      let content = {};
      let actions = [];

      if (action.type === "url" && !response.data.show_screens) {
        dispatch(
          fetchFlow({
            url: action.value,
            formState: {
              ...response.formState,
              ...response.data.hidden_fields
            }
          })
        );
        return;
      } else if (action.type === "openUrl") {
        window.open(action.value);
        dispatch({
          type: INITIALIZE_FLOW,
          payload: {
            ...response,
            data: tdFlow.data,
            initialScreen: tdFlow.data.screens[tdFlow.history[0].index].name
          }
        });
        return;
      } else if (action.type === "screen") {
        dispatch({
          type: INITIALIZE_FLOW,
          payload: {
            ...response,
            data: tdFlow.data,
            url: tdFlow.history[0].url,
            initialScreen: action.value
          }
        });
        return;
      } else if (action.type === "pushAlert" && action.data) {
        const { alert_title, cancel_button_title, message_text } = action.data;
        content = {
          title: alert_title,
          children: message_text
        };
        actions = [
          {
            label: cancel_button_title || tdFlow.settings.translations.cancel,
            className: "secondary",
            name: "close-dialog"
          }
        ];
      } else if (action.data) {
        const { alert_message, alert_title, cancel_button_title } = action.data;
        content = {
          title: alert_title,
          children: alert_message,
          type
        };
        actions = [
          {
            label: cancel_button_title || tdFlow.settings.translations.cancel,
            className: "secondary",
            name: "close-dialog"
          },
          {
            label: tdFlow.settings.translations.okay,
            name: "okay-dialog",
            onClick: () => {
              const formState = { ...tdFlow.formState };
              if (action.value?.endsWith("suicide_alert_accepted")) {
                formState.alert_accepted = true;
                formState.suicide_alert_accepted = true;
                formState.suicide_alert_id = action.data.suicide_alert_id;
              }
              if (action.value?.endsWith("create")) {
                formState.immediate_assistance_alert_accepted = true;
                formState.immediate_assistance_alert_id =
                  action.data.immediate_assistance_alert_id;
              }
              const replaceHistory = (
                tdFlow.data.screens[tdFlow.history[0].index].params || ""
              ).includes("TDScreenOptionReplaceHistoryOnConfirm")
                ? Number.POSITIVE_INFINITY
                : 0;
              dispatch(
                nextScreen(exitScreen, {
                  action: { type, value },
                  formState,
                  replaceHistory
                })
              );
            }
          }
        ];
      } else if (action.value === "dermatologyDialog") {
        dispatch({ type: INITIALIZE_FLOW, payload: response });
        content = {
          title: tdFlow.settings.translations.what_to_expect,
          children: tdFlow.settings.translations.simply_answer
        };
        actions = [
          {
            label: tdFlow.settings.translations.yes_understand,
            name: "understand"
          }
        ];
        setGlobalDialog({
          content,
          actions: enhanceDialogActionsWith(actions, () =>
            dispatch(closeGlobalDialog())
          )
        });
      } else {
        content = {
          title: tdFlow.settings.translations.we_re_sorry,
          children: value
        };
        actions = [{ label: tdFlow.settings.translations.okay }];
      }

      if (action.type === "alert" && response?.data?.screens?.length) {
        dispatch({
          type: INITIALIZE_FLOW,
          payload: {
            ...response,
            replaceHistory: 1
          }
        });
      }

      dispatch(
        setGlobalDialog({
          content,
          actions: enhanceDialogActionsWith(actions, () =>
            dispatch(closeGlobalDialog())
          )
        })
      );
    } else if (
      response.url ===
        "/screens/medical_info/vitals/blood_pressure/added_measurements" ||
      response.url ===
        "/screens/medical_info/vitals/heart_rate/added_measurements" ||
      response.url === "/screens/medical_info/vitals/show_measurements"
    ) {
      // we don't want to have previously added measurements on BloodPressureMeasurementForm
      // in text fields so I'm passing just logged_in_member_id and measurement_type to formState
      dispatch({
        type: INITIALIZE_FLOW,
        payload: {
          ...response,
          formState: {
            logged_in_member_id: response.formState.logged_in_member_id,
            measurement_type: response.formState.measurement_type
          }
        }
      });
    } else {
      dispatch(setGlobalDialog(null));
      dispatch({ type: INITIALIZE_FLOW, payload: response });
    }
  };
}

export const closeGlobalDialog = () => dispatch => {
  dispatch(setGlobalDialog(null));
};

export const initializeTdFlow =
  ({ initialRequest, isPageReloaded }) =>
  (dispatch, getState) => {
    const initialHistory = isPageReloaded ? getState().tdFlow.history : [];
    const currentHistoryItem = initialHistory[0];
    const shouldRefetch =
      isPageReloaded && currentHistoryItem?.reloadPageAction;

    if (shouldRefetch) {
      dispatch(setFlowStatusLoading());
    }

    if (initialRequest) {
      if (isPageReloaded) {
        if (getState().tdFlow.isGlobalDialogShown) {
          dispatch(closeGlobalDialog());
        }

        dispatch({
          type: SET_FORM_ERRORS_FROM_BACK,
          payload: { error: null, errors: null }
        });

        getAllFilesFromIndexedDB().then((files = {}) => {
          const filesKeys = Object.keys(files);

          if (filesKeys.length) {
            const {
              tdFlow: { formState }
            } = getState();
            const matchedFiles = filesKeys.reduce((accumulator, key) => {
              return !formState[key]
                ? { ...accumulator }
                : {
                    ...accumulator,
                    [key]: files[key]
                  };
            }, {});

            const isMatchedFiles = !!Object.keys(matchedFiles).length;

            if (isMatchedFiles) {
              dispatch(fieldChanged({ ...matchedFiles })); // populate files from IndexedDB
            }
          }
        });

        if (!shouldRefetch) {
          return;
        }
      } else {
        clearPersistedStoreInLocalStorage();
        clearIndexedDB();
        dispatch(resetReduxStore());
        dispatch(fieldChanged(initialRequest.formState)); // populate initial state
      }

      const fetchData = {};

      if (!shouldRefetch) {
        fetchData.url = currentHistoryItem?.url || initialRequest.url;
        fetchData.formState =
          getRequestFormState(initialHistory) || initialRequest.formState;
        fetchData.initialScreen =
          currentHistoryItem?.name || initialRequest.initialScreen;
        fetchData.replaceHistory = isPageReloaded
          ? 0
          : Number.POSITIVE_INFINITY;
      } else {
        fetchData.url = currentHistoryItem.reloadPageAction.url;
        fetchData.formState =
          getRequestFormStateByUrl(initialHistory, fetchData.url) ||
          initialRequest.formState;
        fetchData.initialScreen = currentHistoryItem?.name;
        fetchData.replaceHistory = 1;
      }

      // set websdk settings with translations
      getSettings().then(settings => {
        dispatch(setSettings(settings));
        dispatch(
          fetchFlow({
            ...initialRequest,
            ...fetchData,
            isPageReloaded
          })
        );
      });
    }
  };

export function prevScreen(count = 1, clearInput = false) {
  return function (dispatch, getState) {
    dispatch({ type: PREV_SCREEN });

    const { tdFlow } = getState();

    const currentScreen = tdFlow.history[0];
    const previousScreen = tdFlow.history[count];
    const isPrevScreenFromHistory = !currentScreen.returnBackURL;
    const returnBackURL = currentScreen.returnBackURL || previousScreen.url;

    let formState = {};
    let prevFormState = {};

    if (isPrevScreenFromHistory) {
      if (
        previousScreen.url === currentScreen.url &&
        !currentScreen.isInitialScreen
      ) {
        dispatch(prevScreenSuccess({ data: tdFlow.data, count }));
        return;
      }

      const sharedScreensIdentifiers =
        tdFlow?.screenParams?.sharedScreens?.map(screen => screen.identifier) ||
        [];

      if (sharedScreensIdentifiers.includes(previousScreen.identifier)) {
        dispatch(prevScreenSuccess({ data: tdFlow.data, count }));
        return;
      }

      formState = tdFlow.history[clearInput ? count : count - 1].formState;
      prevFormState = getRequestFormState(tdFlow.history, count);

      if (currentScreen.formState.member_id) {
        prevFormState.member_id = currentScreen.formState.member_id;
      }
    } else {
      formState = getRequestDataForNotStoredPrevScreen(currentScreen);
      prevFormState = formState;
    }

    // for prefilling prev screen form from state
    dispatch({ type: SET_STATE, payload: formState });

    return getFlow({
      url: returnBackURL,
      formState: { ...prevFormState, return_back: true }
    })
      .then(res => mapData(res, tdFlow.settings))
      .then(res => {
        const modifiedRes = { ...res };

        if (tdFlow?.screenParams?.sharedScreens) {
          tdFlow.screenParams.sharedScreens.forEach(screen => {
            if (
              !modifiedRes.screens.find(
                resScreen => resScreen.identifier === screen.identifier
              )
            ) {
              modifiedRes.screens.push(screen);
            }
          });
        }

        if (isPrevScreenFromHistory) {
          dispatch(prevScreenSuccess({ data: modifiedRes, count }));
        } else {
          dispatch({
            type: INITIALIZE_FLOW,
            payload: {
              url: returnBackURL,
              data: modifiedRes,
              screenParams: tdFlow?.screenParams,
              formState
            }
          });
        }

        return res;
      })
      .then(data => {
        const internalSdkData = internalSdkAuth(data);
        if (internalSdkData) {
          setEnv({
            AUTHENTICATION_DATA: JSON.stringify({
              internal_sdk: internalSdkData
            })
          });
        }
      })
      .catch(err => {
        devWarn("Error Fetching Prev Screen:", err);
        dispatch(prevScreenFailed(err));
      });
  };
}

export function exitScreen() {
  return function (dispatch, getState) {
    const {
      tdFlow: { initialRequest }
    } = getState();

    dispatch(resetFormState());
    dispatch(
      nextScreen({
        formState: initialRequest.formState,
        action: { type: "url", value: initialRequest.url },
        replaceHistory: Number.POSITIVE_INFINITY
      })
    );
  };
}

export const nextScreen = (
  onExit,
  {
    data,
    screenIndex,
    screenParams,
    formState,
    action,
    replaceHistory = 0,
    initialScreen,
    isUnifiedRegistration
  }
) => {
  tdLog("nextScreen action", action);
  const screenAction = data?.screens?.[screenIndex]?.action || {};
  const theAction = action || screenAction;

  theAction.type = theAction.type ?? screenAction.type;
  theAction.value = theAction.value ?? screenAction.value;

  if (theAction) {
    const { type, value } = theAction;
    const newFormState = theAction?.data
      ? {
          ...formState,
          ...theAction?.data
        }
      : formState;

    switch (type) {
      case "modal_screen":
      case "screen":
        const nextIndex = data.screens.findIndex(screen =>
          [screen.identifier, screen.name].includes(value)
        );

        if (!data.screens[nextIndex]) {
          onExit();
          return {
            type: "UNDEFINED_ACTION"
          };
        }

        const {
          identifier: nextScreenIdentifier,
          name: nextScreenName,
          data: localData = {}
        } = data.screens[nextIndex];
        const { reloadPageAction } = localData;

        return {
          type: NEXT_SCREEN,
          payload: {
            formState: newFormState,
            nextIndex,
            nextScreenIdentifier,
            nextScreenName,
            screenParams,
            replaceHistory,
            reloadPageAction
          }
        };
      case "url":
      case "alert_url":
        clearIndexedDBIfNeeded(formState);

        return fetchFlow({
          url: value,
          formState: {
            ...formState,
            ...theAction?.data
          },
          replaceHistory,
          initialScreen,
          screenParams,
          isUnifiedRegistration
        });
      default:
        return {
          type: "UNDEFINED_ACTION"
        };
    }
  }
};

export function startOver({ url, formState }) {
  return function (dispatch) {
    dispatch({ type: RESET_STATE });
    dispatch(fetchFlow({ url, formState }));
  };
}

export function loadPharmacyResults(requestData, location) {
  return function (dispatch, getState) {
    const storeState = getState();
    const screens = screensSelector(storeState);

    return getPharmacies(requestData, location)
      .then(({ Pharmacies: foundPharmacies }) => {
        if (foundPharmacies.length === 0) {
          dispatch(
            addToast({
              id: SEARCH_PHARMACY_ERROR_TOAST_ID,
              message:
                storeState.tdFlow.settings.translations.no_pharmacies_found
            })
          );
          return;
        }
        dispatch(pharmaciesFound(foundPharmacies));
        dispatch(
          nextScreen(exitScreen, {
            data: storeState.tdFlow.data,
            formState: storeState.tdFlow.formState,
            action: {
              type: "screen",
              value: screens.find(
                s =>
                  s.name === "PharmacyMapScreen" ||
                  s.name === "PharmacyMapTemplatedScreen"
              ).identifier
            }
          })
        );
      })
      .catch(err => {
        dispatch(
          addToast({
            id: SEARCH_PHARMACY_ERROR_TOAST_ID,
            message: err.message
          })
        );
        devWarn("Error Fetching Pharmacies:", err);
      });
  };
}

export function searchRetailPharmacies() {
  return function (dispatch, getState) {
    const storeState = getState();
    const requestData = pharmacyDataSelector(storeState);
    const {
      tdFlow: {
        formState: { searchBy, coords }
      }
    } = getState();
    const locationData =
      coords && searchBy === "Current Location" ? { coords } : null;

    dispatch(loadPharmacyResults(requestData, locationData));
  };
}

export function capsuleDeliverableCheck() {
  return async function (dispatch, getState) {
    const storeState = getState();
    const {
      tdFlow: {
        formState: { searchBy }
      }
    } = storeState;

    const resolveLocation =
      searchBy === "Current Location" ? currentLocation() : null;
    const requestData = pharmacyDataSelector(storeState);
    const location = await Promise.resolve(resolveLocation);

    return capsuleDeliverabilityCheck(requestData, location)
      .then(response => {
        const isDeliverable = !!response.data?.isDeliverable;

        if (!isDeliverable) {
          dispatch(loadPharmacyResults(requestData, resolveLocation));

          return;
        }

        dispatch(capsulePharmacyDelivery(isDeliverable));
        dispatch(
          fetchFlow({
            url: "/screens/request_consult/gm/pharmacy_types",
            formState: {
              ...storeState.tdFlow.formState,
              ...(location && {
                coords: {
                  latitude: location.coords.latitude,
                  longitude: location.coords.longitude
                }
              })
            }
          })
        );
      })
      .catch(() => {
        dispatch(loadPharmacyResults(requestData, resolveLocation));
      });
  };
}

export function searchPharmacies(resolveLocation) {
  return function (dispatch, getState) {
    const storeState = getState();
    const {
      tdFlow: { formState, data, settings }
    } = storeState;

    const requestData = pharmacyDataSelector(storeState);
    const screens = screensSelector(storeState);
    return getPharmacies(requestData, resolveLocation)
      .then(({ Pharmacies: foundPharmacies }) => {
        if (foundPharmacies.length === 0) {
          dispatch(
            addToast({
              id: SEARCH_PHARMACY_ERROR_TOAST_ID,
              message: settings.translations.no_pharmacies_found
            })
          );
          return;
        }
        dispatch(pharmaciesFound(foundPharmacies));

        dispatch(
          nextScreen(exitScreen, {
            data,
            formState,
            action: {
              type: "screen",
              value: screens.find(
                s =>
                  s.params === "TDScreenOptionMapView" ||
                  s.name === "PharmacyMapTemplatedScreen" ||
                  s.name === "PharmacyMapScreen"
              ).identifier
            }
          })
        );
      })
      .catch(err => {
        dispatch(
          addToast({
            id: SEARCH_PHARMACY_ERROR_TOAST_ID,
            message:
              settings.translations["geolocation_error_".concat(err.code)] ||
              err.message
          })
        );
        devWarn("Error Fetching Pharmacies:", err);
      });
  };
}

export function addUUIDToFiles({
  key: fileKey,
  prevFileValue,
  uuid,
  prevFilePhotoName,
  files_upload_field_key,
  otherAttachedImages
}) {
  return function (dispatch) {
    const newFileItem = {
      ...prevFileValue,
      uuid
    };

    dispatch(
      fieldChanged({
        [fileKey]: newFileItem,
        [files_upload_field_key]: [
          ...otherAttachedImages,
          {
            uuid,
            description: prevFilePhotoName
          }
        ]
      })
    );

    setItemToIndexedDB(fileKey, newFileItem);
  };
}

export function uploadPhotoEMO() {
  return function (dispatch, getState) {
    const {
      tdFlow: { formState, screenParams }
    } = getState();
    const { originFieldName } = screenParams;
    const photoName = formState[`__${originFieldName}:photoName`];
    const photoPreview = formState[`__${originFieldName}:photoPreview`];
    dispatch({ type: PREV_SCREEN });
    dispatch(uploadPhotoGeneric(originFieldName, "emo")).then(res => {
      const uuid = res?.data?.uuid;

      if (uuid) {
        const oldUUID = photoPreview.uuid;
        const otherAttachedImages = (formState.files_upload_field || []).filter(
          image => image.uuid !== oldUUID
        );
        dispatch(
          addUUIDToFiles({
            key: `__${originFieldName}:photoPreview`,
            prevFileValue: { ...photoPreview },
            prevFilePhotoName: photoName,
            files_upload_field_key: "files_upload_field",
            otherAttachedImages,
            uuid
          })
        );
      } else {
        dispatch(
          addToast({
            id: UPLOAD_RAV_PHOTO_ERROR_TOAST_ID,
            message: res?.error
          })
        );
        devWarn("Error Uploading photo:", res.error);
      }
      dispatch(prevScreen());
    });
  };
}

export function uploadPhotoMHN() {
  return function (dispatch, getState) {
    const {
      tdFlow: { formState, screenParams }
    } = getState();
    const { originFieldName } = screenParams;
    const photoName = formState[`__${originFieldName}:photoName`];
    const photoPreview = formState[`__${originFieldName}:photoPreview`];
    dispatch({ type: PREV_SCREEN });
    dispatch(uploadPhotoGeneric(originFieldName, "mhn")).then(res => {
      const { uuid } = res?.data;
      if (uuid) {
        const oldUUID = photoPreview.uuid;
        const otherAttachedImages = (formState.attached_images || []).filter(
          image => image.uuid !== oldUUID
        );
        dispatch(
          addUUIDToFiles({
            key: `__${originFieldName}:photoPreview`,
            prevFileValue: { ...photoPreview },
            prevFilePhotoName: photoName,
            files_upload_field_key: "attached_images",
            otherAttachedImages,
            uuid
          })
        );
      } else {
        dispatch(
          addToast({
            id: UPLOAD_RAV_PHOTO_ERROR_TOAST_ID,
            message: res.error
          })
        );
        devWarn("Error Uploading photo:", res.error);
      }
      dispatch(prevScreen());
    });
  };
}

export function uploadPhotoEDIS() {
  return function (dispatch, getState) {
    const {
      tdFlow: { formState, screenParams }
    } = getState();
    const { originFieldName } = screenParams;
    const photoName = formState[`__${originFieldName}:photoName`];
    const photoPreview = formState[`__${originFieldName}:photoPreview`];
    dispatch({ type: PREV_SCREEN });
    dispatch(uploadPhotoGeneric(originFieldName, "edis")).then(res => {
      const { uuid } = res?.data;
      if (uuid) {
        const oldUUID = photoPreview.uuid;
        const otherAttachedImages = (formState.attached_images || []).filter(
          image => image.uuid !== oldUUID
        );
        dispatch(
          addUUIDToFiles({
            key: `__${originFieldName}:photoPreview`,
            prevFileValue: { ...photoPreview },
            prevFilePhotoName: photoName,
            files_upload_field_key: "attached_images",
            otherAttachedImages,
            uuid
          })
        );
      } else {
        dispatch(
          addToast({
            id: UPLOAD_RAV_PHOTO_ERROR_TOAST_ID,
            message: res.error
          })
        );
        devWarn("Error Uploading photo:", res.error);
      }
      dispatch(prevScreen());
    });
  };
}

function allSameAndCorrectType(arr) {
  const newSet = new Set(arr.map(x => typeof x));
  if (newSet.size === 1) {
    if (typeof [...newSet][0] == typeof "") {
      return false;
    }
    return true;
  } else if (newSet.size == 0) {
    return true;
  } else {
    return false;
  }
}

export function uploadPhoto(raw) {
  return function (dispatch, getState) {
    const {
      tdFlow: {
        screenParams,
        formState,
        history,
        data: { screens }
      }
    } = getState();
    const { originFieldName, imagesGroupName, sharedScreens } = screenParams;
    const photoName = formState[`__${originFieldName}:photoName`];
    const photoPreview = formState[`__${originFieldName}:photoPreview`];
    const photoType = formState[`__${originFieldName}:attachment_type_cd`];
    const link = [
      ...(sharedScreens.length >= 1 && allSameAndCorrectType(sharedScreens)
        ? sharedScreens
        : []),
      ...screens
    ].find(s => s.name === history[0].name).action.value;

    // To update description of published photo
    if (
      photoPreview?.uuid?.includes("member_attachment") &&
      !photoPreview.file
    ) {
      const { uuid } = photoPreview;
      const otherAttachedImages = (formState[imagesGroupName] || []).filter(
        image => image.uuid !== uuid
      );

      dispatch(
        fieldChanged({
          [imagesGroupName]: [
            ...otherAttachedImages,
            {
              uuid,
              description: photoName
            }
          ]
        })
      );

      dispatch(prevScreen());
    } else if (photoPreview) {
      dispatch({ type: FETCH_FLOW });
      return uploadPhotoRequest(
        photoName,
        photoPreview,
        photoType,
        formState.member_id,
        raw,
        link
      ).then(res => {
        const uuid = res?.uuid;

        if (uuid) {
          const oldUUID = photoPreview.uuid;
          const otherAttachedImages = (formState[imagesGroupName] || []).filter(
            image => image.uuid !== oldUUID
          );
          dispatch(
            addUUIDToFiles({
              key: `__${originFieldName}:photoPreview`,
              prevFileValue: { ...photoPreview },
              prevFilePhotoName: photoName,
              files_upload_field_key: imagesGroupName,
              otherAttachedImages,
              uuid
            })
          );
        } else {
          const error = res?.error;

          dispatch(
            addToast({
              id: UPLOAD_RAV_PHOTO_ERROR_TOAST_ID,
              message: error
            })
          );
          devWarn("Error Uploading photo:", error);
        }
        dispatch(prevScreen());
      });
    }
  };
}

export function uploadPhotoGeneric(prefix, identifier) {
  return function (dispatch, getState) {
    const {
      tdFlow: { formState }
    } = getState();
    const {
      member_id,
      [`__${prefix}:photoName`]: photoName,
      [`__${prefix}:photoPreview`]: photoPreview,
      [`__${prefix}:attachment_type_cd`]: photoType
    } = formState;
    return uploadPhotoGenericRequest(
      photoName,
      photoPreview,
      photoType,
      member_id,
      identifier
    ).then(res => {
      dispatch(fieldChanged({ [`__${prefix}:photoPreview`]: null }));
      return res;
    });
  };
}

export function uploadPhotoVideoRoom(onUploadSuccess, buttonName) {
  return function (dispatch) {
    dispatch(addCallToActionButtonIsLoading(buttonName));

    dispatch(uploadPhotoGeneric(VIDEO_ROOM_PHOTO_MODAL_FIELD_IDENTIFIER))
      .then(() => {
        onUploadSuccess();
      })
      .catch(() => {
        /**
         * [CIGNA] TO DO (31.08.21):
         * implement error handling
         * */
      })
      .finally(() => {
        dispatch(removeCallToActionButtonIsLoading(buttonName));
      });
  };
}

export function fetchPostFile({
  url,
  openInNewTab = false,
  fileName = "download",
  preparedData,
  buttonName
}) {
  return function (dispatch) {
    if (buttonName) {
      dispatch(addCallToActionButtonIsLoading(buttonName));
    }
    const headers = prepareHttpHeaders();
    return fetch(env("BASE_URL") + url, {
      method: "POST",
      body: JSON.stringify(preparedData),
      responseType: "arraybuffer",
      headers: new Headers(headers)
    })
      .then(response => response.blob())
      .then(blob => {
        const fileURL = URL.createObjectURL(blob);
        if (openInNewTab) {
          window.open(fileURL);
        } else {
          downloadFile(fileURL, fileName);
        }
      })
      .then(() => {
        dispatch(removeToast(DOWNLOADING_FILE_ERROR_TOAST_ID));
      })
      .catch(err => {
        devWarn("Error Downloading File:", err);
        dispatch(
          addToast({
            id: DOWNLOADING_FILE_ERROR_TOAST_ID,
            message: err.message
          })
        );
      })
      .finally(() => {
        if (buttonName) {
          dispatch(removeCallToActionButtonIsLoading(buttonName));
        }
      });
  };
}

export function uploadPhotoMHD() {
  return function (dispatch, getState) {
    const { tdFlow } = getState();
    dispatch({ type: PREV_SCREEN });
    dispatch(uploadPhotoGeneric("medicalInfo"))
      .then(res => {
        const imagesListScreenIndex = tdFlow.data.screens.findIndex(s =>
          (s.params || "").includes("TDScreenOptionImagesListScreen")
        );
        const imagesListScreen = tdFlow.data.screens[imagesListScreenIndex];
        dispatch(
          prevScreenSuccess({
            data: {
              ...tdFlow.data,
              screens: [
                ...tdFlow.data.screens.slice(0, imagesListScreenIndex),
                {
                  ...imagesListScreen,
                  fields: [
                    ...imagesListScreen.fields.filter(
                      f => f.key !== "selectedData"
                    ), // remove old images
                    ...buildImageList(res.screens[0].data.images)
                  ]
                },
                ...tdFlow.data.screens.slice(imagesListScreenIndex + 1)
              ]
            },
            count: 1
          })
        );
      })
      .catch(err => {
        dispatch(prevScreenFailed(err));
      });
  };
}

export function saveMedicalHistory(url, formState) {
  return function (dispatch) {
    dispatch({ type: PREV_SCREEN });
    getFlow({ url, formState })
      .then(() => {
        dispatch(prevScreen());
      })
      .catch(err => {
        devWarn("Error Fetching Prev Screen:", err);
        dispatch(prevScreenFailed(err));
      });
  };
}

export function updateConsultState() {
  return function (dispatch, getState) {
    const {
      tdFlow: {
        formState: { consult_id }
      }
    } = getState();
    getConsultState(consult_id).then(res => {
      dispatch({
        type: SET_CONSULT_STATE,
        payload: res
      });
    });
  };
}

export function updateScreensHistoryFlow(name) {
  return function (dispatch) {
    dispatch({
      type: UPDATE_HISTORY,
      payload: name
    });
  };
}

export function updateScreenHistoryAction(payload) {
  return function (dispatch) {
    dispatch({
      type: UPDATE_HISTORY_ACTION,
      payload
    });
  };
}

export const getCurrentLocation =
  (key, defaultLocationValue = "") =>
  (dispatch, getState) => {
    const {
      tdFlow: { settings }
    } = getState();

    const setLocation = value => {
      const {
        tdFlow: { formState }
      } = getState();

      if (formState[key] === "currentLocation") {
        dispatch(
          fieldChanged({
            [key]: value
          })
        );

        dispatch(resetFieldValidation(key));
      }
    };

    currentLocation()
      .then(locationData => {
        const {
          coords: { latitude, longitude, accuracy }
        } = locationData;

        dispatch(
          fieldChanged({
            consult_location: { latitude, longitude, accuracy }
          })
        );

        return getLocationObjForCoordinates(locationData);
      })
      .then(response => {
        setLocation(extractLocationName(response, key, settings));
      })
      .catch(() => setLocation(defaultLocationValue));
  };

/*
 * WBSDK-1467
 * TO DO:
 * sendTrackingEvents() can be removed due to changing the logic of the sending of TrackingEvent
 */
export function sendTrackingEvents() {
  return function (dispatch, getState) {
    const {
      tdFlow: { trackingEvents }
    } = getState();
    if (trackingEvents && trackingEvents.length > 0) {
      logTrackingEvents(trackingEvents)
        .then(function () {
          dispatch({ type: RESET_TRACKING_EVENTS });
        })
        .catch(function (e) {
          // eslint-disable-next-line no-console
          console.log(e);
        });
    }
  };
}

export const fetchAvailableTimes =
  ({ action: { value: url = "" }, newData = {}, runningFieldName = "" }) =>
  async (dispatch, getState) => {
    const {
      tdFlow: { formState, settings }
    } = getState();

    const actualFormState = {
      ...formState,
      ...newData
    };

    // We can't request a timeslots without 'date'
    if (!actualFormState.date) {
      return;
    }

    // If 'time_zone' is updated, then we should update 'date' ragarding to new 'time_zone' and 'selectedDate'
    if (newData.time_zone) {
      const {
        tdFlow: {
          dateTimeSelection: { selectedDate }
        }
      } = getState();

      const dateFieldName = "date";
      const adjastedDate = convertToUtc(selectedDate, newData.time_zone);

      dispatch(
        fieldChanged({
          [dateFieldName]: adjastedDate
        })
      );

      actualFormState[dateFieldName] = adjastedDate;
    }

    // If 'time_zone' is absent, we must set LOCAL(default) time_zone
    if (!actualFormState.time_zone) {
      actualFormState.time_zone = getLocalTimeZone();
    }

    dispatch({ type: FETCH_AVAILABLE_TIMES_PENDING });
    dispatch(
      fieldChanged({
        [runningFieldName]: ""
      })
    );

    return getFlow({
      url,
      formState: actualFormState
    })
      .then(res => {
        // For now response is always an array with times like ["00:30"], however...
        const availableTimes = res instanceof Array ? res : [];

        dispatch({
          type: FETCH_AVAILABLE_TIMES_SUCCESS,
          payload: { availableTimes }
        });
      })
      .catch(error => {
        const errorMessage = getAdjustedToCignaErrorText(error, settings);

        dispatch({
          type: FETCH_AVAILABLE_TIMES_FAILURE,
          payload: { error: errorMessage }
        });
        dispatch(
          addToast({
            id: FETCH_AVAILABLE_TIMES_ERROR_TOAST_ID,
            message: errorMessage
          })
        );
      });
  };
